import { logMetaExperiment } from 'experiments/Flights_meta_ow_fis_to_fsr_46906/experiment_helper_46906';
import { ClientLogger } from 'bernie-client';
import { getBexApiContext } from 'bernie-context';
import { codeSplit, Controller, FetchOptions, FetchPageOptions, StoreContainer } from 'bernie-core';
import { AnalyticsStore, ContextStore, ExperimentStore, PageStore } from 'bernie-plugin-mobx';
import { Localization } from 'bernie-l10n';

import {
  FlightInformationStore,
  FreeCancellationStore,
  InformationMetaCriteriaStore,
  InformationResultStore,
  MessagingCardsStore,
  BannersStore,
  PriceSummaryStore,
  ErrorStore,
  ClientMetadataStore,
  PageTitleStore,
  FlightsSelectedJourneyReviewStore,
  OpinionLabStore,
  StepIndicatorStore,
  DetailsAndFaresStore,
  JourneyContinuationIdStore,
  GaiaIdStore,
  JourneyStartEndDatesStore,
  FlightsSupportedAncillariesStore,
  PartnerEarnCardStore,
  SearchCriteriaStore,
  SearchFormStore,
  ListingsStore,
  SearchResultsStore,
  SortFilterStore,
  FlexibleSearchResultsStore,
  AdditionalShoppingStore,
  InformationCriteriaStore
} from 'stores';
import { generateRequestOptions, getPageName, isFlightsPredictiveSearch, PageId } from 'utility';
import { DynamicElementsStore } from 'stores/dynamic-elements-store';
import { FlightsQueryState, FlightsShoppingPwaFlightSearchResultsFlightsSearch } from '__generated__/api';
import { DetailsController } from 'controllers/details-controller';
import { getNavigationIdFromQuery } from 'utility/navigation-identity';
import { FlightsMetaFIStoFSR } from 'experiments/Flights_meta_ow_fis_to_fsr_46906';
import { SystemEvent, SystemEventLevel } from 'bernie-logger';

export class FlightInformationMetaController implements Controller {
  public pageId = PageId.FLIGHT_INFORMATION; // TODO: check
  public path = '/Details*';
  public routeName = 'details';
  public bundles = [];
  public exact = true;
  public component = codeSplit(() => import(/* webpackChunkName: "information" */ '../views/flight-information'));
  public pendingPromises = [];
  public clientErrorRenderDelay = 500;
  private logger = ClientLogger.getLoggerWithIdentifier('controller.flightInformationMeta');
  public meetsMetaExperimentConditions = false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public fetch(options: FetchOptions): Promise<any> {
    if (options.isServer) {
      return this.fetchServerSide(options);
    }

    return;
  }

  public fetchPageData(pageData: FetchPageOptions) {
    const localizer = new Localization(pageData.context.locale);
    const query = pageData.query;
    const title = localizer.formatText('fis.server.page.title');
    const pageName = getPageName(this.pageId);
    const navigationId = getNavigationIdFromQuery(query);

    return Promise.resolve({ title, pageName, navigationId });
  }

  private async fetchServerSide(options: FetchOptions) {
    const {
      flightInformationStore,
      informationResultStore,
      informationCriteriaStore,
      analyticsStore,
      freeCancellationStore,
      informationMetaCriteriaStore,
      priceSummaryStore,
      errorStore,
      clientMetadataStore,
      pageTitleStore,
      metaVariables,
      flightsSelectedJourneyReviewStore,
      stepIndicatorStore,
      gaiaIdStore,
      journeyStartEndDatesStore,
      flightsSupportedAncillariesStore,
      experimentStore,
      bernieContext,
      pageStore,
      searchCriteriaStore,
      searchFormStore,
      listingsStore,
      searchResultsStore,
      sortFilterStore,
      variables,
      flexibleSearchResultsStore,
      bannersStore,
      journeyContinuationIdStore,
      additionalShoppingStore
    } = this.getFetchVariables(options.stores, options.isServer);

    try {
      this.meetsMetaExperimentConditions = await FlightsMetaFIStoFSR.meetsMetaExperimentConditions(options);
    } catch (error) {
      this.logger.logEvent(new SystemEvent(SystemEventLevel.ERROR, 'FLIGHT_DETAIL_META'), error);
    }

    if (this.meetsMetaExperimentConditions) {
      logMetaExperiment.logExperiment(bernieContext, experimentStore, pageStore);
      this.logger.logEvent(
        new SystemEvent(SystemEventLevel.INFO, 'FLIGHT_DETAIL_META_EXPERIMENT'),
        JSON.stringify('isMetaExperimentVariant: ' + logMetaExperiment.isMetaExperimentEnabled(experimentStore))
      );
    }

    if (this.meetsMetaExperimentConditions && logMetaExperiment.isMetaExperimentEnabled(experimentStore)) {
      try {
        FlightsMetaFIStoFSR.setMetaExperimentCriteriaFromUrl(options, searchCriteriaStore, true);
      } catch (error) {
        this.logger.logEvent(new SystemEvent(SystemEventLevel.ERROR, 'FLIGHT_DETAIL_META'), error);
      }

      const response = await searchResultsStore.fetchLoading(
        variables,
        generateRequestOptions(options.request, options.isServer, PageId.FLIGHTS_SEARCH_ONE_WAY),
        analyticsStore,
        isFlightsPredictiveSearch(options)
      );

      if (isFlightsPredictiveSearch(options) || !response) {
        return;
      }

      const {
        searchForm,
        sortAndFilterResult,
        listingResult,
        pageTitle,
        clientMetadata,
        gaiaId,
        stepIndicator,
        flexibleSearchResponse,
        journeysContinuationId
      } = response;

      const journeyContinuationId = journeysContinuationId;

      searchFormStore.hydrate({ searchForm });
      listingsStore.hydrate({ listingResult });
      pageTitleStore.hydrate({ pageTitle });
      sortFilterStore.hydrate({ sortAndFilterResult });
      clientMetadataStore.hydrate({ clientMetadata });
      gaiaIdStore.hydrate({ gaiaId });
      stepIndicatorStore.hydrate({ stepIndicator });
      searchCriteriaStore.forceFetch = false;
      searchCriteriaStore.newSearch = true;
      searchCriteriaStore.setIsMetaExperiment(true);
      flexibleSearchResultsStore.showGridFlag = true;
      listingsStore.setLoading(true);
      bannersStore.refreshBanner = !bannersStore.refreshBanner;
      journeyContinuationIdStore.hydrate({ journeyContinuationId });
      flexibleSearchResultsStore.hydrate(
        Object.assign(FlexibleSearchResultsStore.DEFAULTS, {
          flexibleSearchResponse: {
            subTitle: flexibleSearchResponse?.subTitle,
            title: flexibleSearchResponse?.title,
            titleAccessibility: flexibleSearchResponse?.titleAccessibility,
            searchTriggerAction: flexibleSearchResponse?.searchTriggerAction,
            flexGridType: flexibleSearchResponse?.flexGridType,
            loadingStateAccessibility: flexibleSearchResponse?.loadingStateAccessibility
          }
        })
      );
      errorStore.noApiResponse = false;
      sortFilterStore.showFiltersSkeleton = true;
      if (listingsStore.listingResult.paginationConfig) {
        searchCriteriaStore.updateSearchCriteriaWithPagination(
          listingsStore.listingResult.paginationConfig[0]?.pagination
        );
        searchCriteriaStore.updateSearchCriteriaWithFaresSeparation(
          listingsStore.listingResult.paginationConfig[0]?.faresSeparationType
        );
        Object.assign(variables, searchCriteriaStore.searchCriteria);
      }
      searchResultsStore.setFlightsSearchResultLoadedRequest(
        searchResultsStore.createFlightsSearchLoadedRequest(options, variables, PageId.FLIGHTS_SEARCH_ONE_WAY)
      );
    } else {
      informationMetaCriteriaStore.setCriteriaFromUrl(options);

      const response = await informationResultStore.fetchLoading(
        metaVariables,
        generateRequestOptions(options.request, true, this.pageId),
        analyticsStore
      );

      if (!response) {
        return;
      }
      DetailsController.hydrateDetailsStores(
        response,
        freeCancellationStore,
        flightsSelectedJourneyReviewStore,
        priceSummaryStore,
        errorStore,
        clientMetadataStore,
        pageTitleStore,
        stepIndicatorStore,
        gaiaIdStore,
        journeyStartEndDatesStore,
        flightsSupportedAncillariesStore,
        additionalShoppingStore
      );
      const { journeyContinuationId } = response;
      journeyContinuationIdStore.hydrate({ journeyContinuationId });
      informationCriteriaStore.hydrate({
        informationCriteria: {
          flightsDetailCriteria: { journeyContinuationId }
        },
        sideSheetQueryContext: { journeyContinuationId }
      });
      flightInformationStore.loading = true;
      errorStore.noApiResponse = false;
    }
  }

  private getFetchVariables(stores: StoreContainer, isServer: boolean) {
    const bernieContext: ContextStore = stores.get<ContextStore>('context');
    const analyticsStore = stores.get<AnalyticsStore>('analytics');
    const context = getBexApiContext(bernieContext);
    const informationMetaCriteriaStore = stores.get<InformationMetaCriteriaStore>('informationMetaCriteriaStore');
    const informationCriteriaStore = stores.get<InformationCriteriaStore>('informationCriteriaStore');
    informationMetaCriteriaStore.informationCriteria.queryState = FlightsQueryState.LOADING;
    const experimentStore = stores.get<ExperimentStore>('experiment');
    const pageStore = stores.get<PageStore>('page');

    const metaVariables = {
      context,
      ...informationMetaCriteriaStore.informationCriteria
    };
    const flightInformationStore = stores.get<FlightInformationStore>('flightInformationStore');
    const informationResultStore = stores.get<InformationResultStore>('informationResult');
    const freeCancellationStore = stores.get<FreeCancellationStore>('freeCancellationStore');
    const messagingCardsStore = stores.get<MessagingCardsStore>('messagingCardsStore');
    const bannersStore = stores.get<BannersStore>('bannersStore');
    const priceSummaryStore = stores.get<PriceSummaryStore>('priceSummaryStore');
    const errorStore = stores.get<ErrorStore>('errorStore');
    const clientMetadataStore = stores.get<ClientMetadataStore>('clientMetadataStore');
    const pageTitleStore = stores.get<PageTitleStore>('pageTitleStore');
    const opinionLabStore = stores.get<OpinionLabStore>('opinionLabStore');
    const flightsSelectedJourneyReviewStore = stores.get<FlightsSelectedJourneyReviewStore>(
      'flightsSelectedJourneyReviewStore'
    );
    const dynamicElementsStore = stores.get<DynamicElementsStore>('dynamicElementsStore');
    const stepIndicatorStore = stores.get<StepIndicatorStore>('stepIndicatorStore');
    const detailsAndFaresStore = stores.get<DetailsAndFaresStore>('detailsAndFaresStore');
    const journeyContinuationIdStore = stores.get<JourneyContinuationIdStore>('journeyContinuationIdStore');
    const gaiaIdStore = stores.get<GaiaIdStore>('gaiaIdStore');
    const journeyStartEndDatesStore = stores.get<JourneyStartEndDatesStore>('journeyStartEndDatesStore');
    const flightsSupportedAncillariesStore = stores.get<FlightsSupportedAncillariesStore>(
      'flightsSupportedAncillariesStore'
    );
    const partnerEarnCardStore = stores.get<PartnerEarnCardStore>('partnerEarnCardStore');
    const additionalShoppingStore = stores.get<AdditionalShoppingStore>('additionalShoppingStore');

    const searchCriteriaStore = stores.get<SearchCriteriaStore>('searchCriteriaStore');
    const searchFormStore = stores.get<SearchFormStore>('searchFormStore');
    const listingsStore = stores.get<ListingsStore>('listingsStore');
    const searchResultsStore = stores.get<SearchResultsStore>('searchResults');
    const sortFilterStore = stores.get<SortFilterStore>('sortFilterStore');
    const flexibleSearchResultsStore = stores.get<FlexibleSearchResultsStore>('flexibleSearchResultsStore');
    const loadSwitch = isServer ? FlightsQueryState.LOADING : FlightsQueryState.LOADED;

    searchCriteriaStore.searchCriteria.queryState = loadSwitch;

    const variables = {
      context,
      rewardsOption: searchCriteriaStore.rewardsOption,
      ...searchCriteriaStore.searchCriteria
    } as FlightsShoppingPwaFlightSearchResultsFlightsSearch.Variables;

    return {
      experimentStore,
      bernieContext,
      pageStore,
      analyticsStore,
      metaVariables,
      flightInformationStore,
      informationResultStore,
      informationMetaCriteriaStore,
      informationCriteriaStore,
      freeCancellationStore,
      messagingCardsStore,
      bannersStore,
      priceSummaryStore,
      errorStore,
      clientMetadataStore,
      pageTitleStore,
      opinionLabStore,
      flightsSelectedJourneyReviewStore,
      dynamicElementsStore,
      stepIndicatorStore,
      detailsAndFaresStore,
      journeyContinuationIdStore,
      gaiaIdStore,
      journeyStartEndDatesStore,
      flightsSupportedAncillariesStore,
      partnerEarnCardStore,
      searchCriteriaStore,
      searchFormStore,
      listingsStore,
      searchResultsStore,
      sortFilterStore,
      variables,
      flexibleSearchResultsStore,
      additionalShoppingStore
    };
  }
}
